'use client';

import { useState } from 'react';

import { Bot, Check, Crown, ShieldCheck, Sparkles } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { getAiChatCopy } from '@/libs/ai-chat-i18n';
import { getAiChatModelAccessCopy } from '@/libs/ai-chat-model-access-i18n';
import { AI_CHAT_TEXT_MODELS, groupAiChatModelsByOrganization } from '@/libs/ai-chat-models';
import type { PricingCopy } from '@/libs/pricing-i18n';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';

type Props = {
  locale: string;
  pricingCopy: PricingCopy['chat'];
  plusMonthlyPriceUsd: number;
  proMonthlyPriceUsd: number;
};

const modelGroups = groupAiChatModelsByOrganization(AI_CHAT_TEXT_MODELS);

export default function AiChatPricing({
  locale,
  pricingCopy,
  plusMonthlyPriceUsd,
  proMonthlyPriceUsd,
}: Props) {
  const copy = getAiChatCopy(locale);
  const accessCopy = getAiChatModelAccessCopy(locale);
  const chatHref = locale === 'en' ? '/ai-chat' : `/${locale}/ai-chat`;
  const router = useRouter();
  const [checkoutLoading, setCheckoutLoading] = useState<'plus' | 'pro' | null>(null);
  const [checkoutError, setCheckoutError] = useState<{ plan: 'plus' | 'pro'; message: string } | null>(null);

  async function subscribe(plan: 'plus' | 'pro') {
    setCheckoutLoading(plan);
    setCheckoutError(null);
    try {
      const supabase = createClientComponentClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        router.push(`${locale === 'en' ? '/signin' : `/${locale}/signin`}?returnTo=${encodeURIComponent(chatHref)}`);
        return;
      }

      const response = await fetch('/api/ai-chat/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale, plan }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.url) throw new Error(payload?.error || copy.generalError);
      window.location.assign(payload.url);
    } catch (error: any) {
      setCheckoutError({ plan, message: error?.message || copy.generalError });
      setCheckoutLoading(null);
    }
  }

  return (
    <section id="ai-chat-plans" className="scroll-mt-28 pt-14 sm:pt-16" aria-labelledby="ai-chat-pricing-heading">
      <div className="grid gap-7 border-b border-[color:var(--editorial-line)] pb-8 lg:grid-cols-[minmax(0,1fr)_minmax(300px,.62fr)] lg:items-end">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--editorial-accent-dark)]">{copy.kicker}</p>
            <span className="rounded-full border border-[color:var(--editorial-line)] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--editorial-muted)]">{pricingCopy.membership}</span>
          </div>
          <h1 id="ai-chat-pricing-heading" className="font-editorial mt-4 max-w-3xl text-4xl font-semibold tracking-[-0.035em] text-[color:var(--editorial-ink)] sm:text-5xl">
            {pricingCopy.title}
          </h1>
        </div>
        <p className="max-w-[48ch] text-base leading-7 text-[color:var(--editorial-muted)] lg:justify-self-end">
          {pricingCopy.intro}
        </p>
      </div>

      <div className="mt-8 rounded-[28px] border border-[color:var(--editorial-line)] bg-white/70 p-2 shadow-[0_22px_70px_rgba(16,35,63,.07)] sm:p-3">
        <div className="grid items-stretch gap-2 lg:grid-cols-3">
          <article className="group flex h-full flex-col rounded-[22px] border border-transparent bg-white p-6 transition duration-200 hover:-translate-y-0.5 hover:border-[color:var(--editorial-line)] sm:p-8">
            <div className="flex min-h-8 items-center justify-between gap-4">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--editorial-muted)]">{pricingCopy.startHere}</span>
              <span className="rounded-full border border-[color:var(--editorial-line)] px-3 py-1 text-[11px] font-semibold text-[color:var(--editorial-muted)]">{pricingCopy.noCard}</span>
            </div>
            <h3 className="font-editorial mt-7 text-4xl font-semibold tracking-[-0.035em] text-[color:var(--editorial-ink)]">Free</h3>
            <p className="mt-2 min-h-12 text-sm leading-6 text-[color:var(--editorial-muted)]">{pricingCopy.standardDescription}</p>

            <div className="mt-7 flex items-end gap-3 border-b border-[color:var(--editorial-line)] pb-7">
              <span className="price-figures text-6xl font-semibold tracking-[-0.055em] text-[color:var(--editorial-ink)]">$0</span>
              <span className="mb-2 text-sm font-semibold uppercase tracking-[0.12em] text-[color:var(--editorial-muted)]">{pricingCopy.usd}</span>
            </div>

            <ul className="my-7 flex-1 space-y-3.5" aria-label={`Free ${pricingCopy.includes}`}>
              {pricingCopy.standardFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm leading-6 text-slate-700">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--editorial-ink)] text-white"><Check className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" /></span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <Link href={chatHref} className="editorial-button editorial-button-secondary flex min-h-12 w-full items-center justify-center gap-2 px-5 text-sm font-semibold">
              <Bot className="h-4 w-4" aria-hidden="true" />
              {pricingCopy.startChat}
            </Link>
          </article>

          <article className="relative flex h-full flex-col overflow-hidden rounded-[22px] border border-[color:var(--editorial-accent)] bg-[color:var(--editorial-ink)] p-6 text-white shadow-[0_24px_60px_rgba(16,35,63,0.18)] transition duration-200 hover:-translate-y-0.5 sm:p-8">
            <div className="flex min-h-8 items-center justify-between gap-4">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/65">{pricingCopy.regularUse}</span>
              <span className="rounded-full bg-[color:var(--editorial-accent)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-white">{pricingCopy.allModelsBadge}</span>
            </div>
            <h3 className="font-editorial mt-7 text-4xl font-semibold tracking-[-0.035em] text-white">Plus</h3>
            <p className="mt-2 min-h-12 text-sm leading-6 text-white/68">{pricingCopy.plusDescription}</p>

            <div className="mt-7 flex flex-wrap items-end gap-x-3 gap-y-1 border-b border-white/20 pb-7">
              <span className="price-figures text-6xl font-semibold tracking-[-0.055em] text-white">${plusMonthlyPriceUsd.toFixed(1)}</span>
              <span className="mb-2 text-sm font-semibold uppercase tracking-[0.12em] text-white/65">{pricingCopy.perMonth}</span>
            </div>

            <ul className="my-7 flex-1 space-y-3.5" aria-label={`Plus ${pricingCopy.includes}`}>
              {pricingCopy.plusFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm leading-6 text-white/85">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--editorial-accent)] text-white"><Check className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" /></span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button type="button" onClick={() => void subscribe('plus')} disabled={checkoutLoading !== null} className="editorial-button editorial-button-primary flex min-h-12 w-full items-center justify-center gap-2 px-5 text-sm font-semibold disabled:cursor-wait disabled:opacity-70">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              {checkoutLoading === 'plus' ? copy.subscribing : pricingCopy.getPlus}
            </button>
            {checkoutError?.plan === 'plus' && <p className="mt-3 text-center text-xs leading-5 text-red-200" role="alert">{checkoutError.message}</p>}
          </article>

          <article className="flex h-full flex-col rounded-[22px] border border-transparent bg-[color:var(--editorial-paper)] p-6 transition duration-200 hover:-translate-y-0.5 hover:border-[color:var(--editorial-line)] sm:p-8">
            <div className="flex min-h-8 items-center justify-between gap-4">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--editorial-muted)]">{pricingCopy.demandingWork}</span>
              <span className="rounded-full border border-[color:var(--editorial-accent)] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[color:var(--editorial-accent-dark)]">{pricingCopy.proAllowanceBadge}</span>
            </div>
            <h3 className="font-editorial mt-7 text-4xl font-semibold tracking-[-0.035em] text-[color:var(--editorial-ink)]">Pro</h3>
            <p className="mt-2 min-h-12 text-sm leading-6 text-[color:var(--editorial-muted)]">{pricingCopy.proDescription}</p>

            <div className="mt-7 flex flex-wrap items-end gap-x-3 gap-y-1 border-b border-[color:var(--editorial-line)] pb-7">
              <span className="price-figures text-6xl font-semibold tracking-[-0.055em] text-[color:var(--editorial-ink)]">${proMonthlyPriceUsd.toFixed(1)}</span>
              <span className="mb-2 text-sm font-semibold uppercase tracking-[0.12em] text-[color:var(--editorial-muted)]">{pricingCopy.perMonth}</span>
            </div>

            <ul className="my-7 flex-1 space-y-3.5" aria-label={`Pro ${pricingCopy.includes}`}>
              {pricingCopy.proFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm leading-6 text-slate-700">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[color:var(--editorial-ink)]"><Check className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" /></span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button type="button" onClick={() => void subscribe('pro')} disabled={checkoutLoading !== null} className="editorial-button editorial-button-primary flex min-h-12 w-full items-center justify-center gap-2 px-5 text-sm font-semibold disabled:cursor-wait disabled:opacity-70">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              {checkoutLoading === 'pro' ? copy.subscribing : pricingCopy.getPro}
            </button>
            {checkoutError?.plan === 'pro' && <p className="mt-3 text-center text-xs leading-5 text-red-600" role="alert">{checkoutError.message}</p>}
          </article>
        </div>
      </div>
      <p className="mt-6 text-sm leading-6 text-[color:var(--editorial-muted)]">{pricingCopy.usageNote}</p>
      <section className="mt-8 border-t border-[color:var(--editorial-line)] py-6" aria-labelledby="ai-chat-models-heading">
        <h2 id="ai-chat-models-heading" className="text-xl font-semibold text-[color:var(--editorial-ink)]">
          {pricingCopy.modelsTitle}
        </h2>
        <p className="mt-2 text-sm leading-6 text-[color:var(--editorial-muted)]">{pricingCopy.modelsNote}</p>
        <div className="mt-5 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {modelGroups.map(({ organization, models }) => (
            <div key={organization} className="min-w-0">
              <h3 className="text-base font-semibold text-[color:var(--editorial-ink)]">{organization}</h3>
              <ul className="mt-3 space-y-3">
                {models.map((model) => (
                  <li key={model.id} className="flex flex-wrap items-center justify-between gap-2 text-base leading-6 text-[color:var(--editorial-ink)]">
                    <span>{model.name}</span>
                    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${model.free ? 'border-slate-200 bg-white text-slate-600' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                      {!model.free && <Crown className="h-3.5 w-3.5" aria-hidden="true" />}
                      {model.free ? pricingCopy.freeModelBadge : accessCopy.paidBadge}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}
